import { getToken } from "firebase/messaging";

export default function useFirebase() {
  const supabase = useSupabaseClient();

  async function updateTokensInDatabase() {
    useLogger().log("Updating FCM tokens in DB.");

    const nuxtApp = useNuxtApp();
    const runtimeConfigPublic = useRuntimeConfig().public;

    const messaging = nuxtApp.$messaging;
    if (!messaging) {
      useLogger().log(
        "updateTokensInDatabase: $messaging is not available (Firebase config may be missing, e.g. FIREBASE_PROJECT_ID)."
      );
      console.error(
        "updateTokensInDatabase: Firebase Messaging is not initialized. Check that Firebase env vars are set in nuxt.config (e.g. FIREBASE_PROJECT_ID)."
      );
      return;
    }

    const vapidKey = runtimeConfigPublic.firebaseVapidKey;
    if (!vapidKey || typeof vapidKey !== "string") {
      useLogger().log("updateTokensInDatabase: firebaseVapidKey is missing.");
      console.error(
        "updateTokensInDatabase: FIREBASE_VAPID_KEY is not set in runtime config. Add it to .env and nuxt.config public runtimeConfig."
      );
      return;
    }

    try {
      let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
      if (import.meta.client && "serviceWorker" in navigator) {
        serviceWorkerRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );
        await navigator.serviceWorker.ready;
      }

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration,
      });

      useLogger().log(`FCM token: ${token}`);

      const userStore = useUserStore();
      const currentUser = userStore.user;

      if (currentUser == null) {
        useLogger().log(`currentUser == null`);
        throw Error("currentUser == null");
      }

      const newTokensSet = new Set(userStore.notificationTokens);
      useLogger().log(`Existing FCM tokens: ${newTokensSet}`);

      newTokensSet.add(token);

      if (userStore.notificationTokens.length > 0) {
        const { data, error } = await supabase
          .from("notification_tokens")
          .update({
            updated_at: new Date().toISOString(),
            tokens: Array.from(newTokensSet),
          })
          .eq("user_id", currentUser.id)
          .select("tokens");

        if (data) {
          userStore.notificationTokens = data[0].tokens ?? [];
          useLogger().log(`New token updated.`);
        }
      } else {
        const { data, error } = await supabase
          .from("notification_tokens")
          .insert({
            user_id: currentUser.id,
            tokens: Array.from(newTokensSet),
          })
          .select("tokens");

        if (data) {
          userStore.notificationTokens = data[0].tokens ?? [];
          useLogger().log(`New token inserted.`);
        }
      }
    } catch (error) {
      useLogger().log(`updateTokensError`, (error as Error).message);
      const err = error as Error;
      console.error("updateTokensError", error);

      if (err.name === "AbortError") {
        console.error(
          "Push subscription failed. Check FIREBASE_VAPID_KEY and ensure it belongs to the same Firebase project as FIREBASE_PROJECT_ID.",
        );
      }
    }
  }

  function onMessage(payload: string) {
    useLogger().log(`New push notification message arrived:`, payload);

    // Don't display toast if user is on messages view already.
    const currentRoute = useRoute().fullPath;
    const isCurrentRouteMessages = currentRoute == "/action-center";
    if (isCurrentRouteMessages) {
      return;
    }

    const payloadObj = JSON.parse(payload);
    const title = payloadObj.notification.title;
    const body = payloadObj.notification.body;

    useToast().add({
      title: title,
      description: body,
      avatar: {
        src: payloadObj.data.authorAvatarUrl,
        alt: title.toUpperCase(),
      },
      ui: {
        description: "truncate",
      },
      click: async () => {
        useToast().clear();

        const messagesStore = useMessagesStore();

        if (!messagesStore.unreadMessagesCount) {
          messagesStore.unreadMessagesCount = 0;
        }

        messagesStore.unreadMessagesCount += 1;
        messagesStore.pushNotificationToastChatId = payloadObj.data.chat_id;
        await navigateTo("/action-center");
      },
    });
  }

  return {
    onMessage,
    updateTokensInDatabase,
  };
}
